import {
  NetworkValue,
  StableCoinValue,
  getSupportedCurrencies,
} from './currencies'

export type UserTokenPreference = {
  stableCoin: StableCoinValue
  network: NetworkValue
  lastUpdated: number
}

const PREFERENCE_KEY_PREFIX = 'paytrie_user_preference'
const PREFERENCE_TTL = 30 * 24 * 60 * 60 * 1000 // 30 days

const getUserPreferenceKey = (userId: string): string => {
  return `${PREFERENCE_KEY_PREFIX}_${userId}`
}

const isValidPreference = (
  stableCoin: StableCoinValue,
  network: NetworkValue,
  enableCadcSolana: boolean
): boolean => {
  const supportedCurrencies = getSupportedCurrencies(enableCadcSolana)
  return (
    supportedCurrencies[network] &&
    supportedCurrencies[network].includes(stableCoin)
  )
}
export const saveUserTokenPreference = (
  userId: string,
  stableCoin: StableCoinValue,
  network: NetworkValue,
  enableCadcSolana: boolean
): void => {
  if (!userId) {
    console.warn('Cannot save preference: userId is required')
    return
  }

  if (!isValidPreference(stableCoin, network, enableCadcSolana)) {
    console.warn(
      `Cannot save preference: ${stableCoin} is not supported on ${network}`
    )
    return
  }

  try {
    const preference: UserTokenPreference = {
      stableCoin,
      network,
      lastUpdated: Date.now(),
    }

    const key = getUserPreferenceKey(userId)
    localStorage.setItem(key, JSON.stringify(preference))
  } catch (error) {
    console.error('Failed to save user preference:', error)
  }
}

export const getUserTokenPreference = (
  userId: string,
  enableCadcSolana: boolean
): UserTokenPreference | null => {
  if (!userId) {
    return null
  }

  try {
    const key = getUserPreferenceKey(userId)
    const stored = localStorage.getItem(key)

    if (!stored) {
      return null
    }

    const preference: UserTokenPreference = JSON.parse(stored)

    if (Date.now() - preference.lastUpdated > PREFERENCE_TTL) {
      removeUserTokenPreference(userId)
      return null
    }

    if (
      !isValidPreference(
        preference.stableCoin,
        preference.network,
        enableCadcSolana
      )
    ) {
      console.warn('Saved preference is no longer valid, removing:', preference)
      removeUserTokenPreference(userId)
      return null
    }

    return preference
  } catch (error) {
    console.error('Failed to retrieve user preference:', error)
    return null
  }
}

export const removeUserTokenPreference = (userId: string): void => {
  if (!userId) {
    return
  }

  try {
    const key = getUserPreferenceKey(userId)
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Failed to remove user preference:', error)
  }
}

export const clearAllUserPreferences = (): void => {
  try {
    const keysToRemove: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(PREFERENCE_KEY_PREFIX)) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch (error) {
    console.error('Failed to clear user preferences:', error)
  }
}
